
import { defineComponent, ref } from "vue";
import {
  required,
  maxLength,
  helpers,
  sameAs,
  minLength,
  email,
} from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import ToolTip from "@/components/common/ToolTip.vue";
import routesPath from "@/config/routesPath";
import * as apiClient from "@/apiclient/";
import TokenService from "@/services/tokenService";
import messages from "@/config/messages";
import common from "@/config/common";
import { mapMutations, mapGetters } from "vuex";
import validateDob from "@/utils/validations/validateDob";

interface UserData {
  ssn: string;
  zip: string;
  dob: string;
  lastName: string;
  fullName: string;
  userName: string;
  password: string;
  passwordConfirmation: string;
  email: string;
  securityQuestion: string;
  securityAnswer: string;
}

export default defineComponent({
  name: "CreateAccount",
  components: {
    ToolTip,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      user: {
        ssn: "",
        zip: "",
        dob: "",
        lastName: "",
        fullName: "",
        userName: "",
        password: "",
        passwordConfirmation: "",
        email: "",
        securityQuestion: "",
        securityAnswer: "",
      } as UserData,

      submitted: false,
      isFormInvalid: false,
      validationMsg: "",
      isSubmitDisabled: false,
      messages,
      loginResponse: {} as apiClient.LoginResponse,
      routesPath,
      step: 1,
      createAccountToken: "",
      accountCreated: false,
    };
  },
  validations() {
    const isSearchStep = this.step === 1;
    const isCreateStep = this.step === 2;

    return {
      user: {
        ssn: {
          ...(isSearchStep && { required }),
        },
        zip: {
          ...(isSearchStep && { required }),
        },
        dob: {
          ...(isSearchStep && { required, validateDob }),
        },
        lastName: {
          ...(isSearchStep && { required }),
        },
        userName: {
          ...(isCreateStep && {
            required,
            maxLength: maxLength(15),
          }),
        },
        password: {
          ...(isCreateStep && {
            required,
            differentThanSecurityQuestionAnswerValidation: helpers.withMessage(
              "Password cannot be the same as the security answer",
              () => {
                const { password, securityAnswer } = this.user;
                return (
                  password === "" ||
                  securityAnswer === "" ||
                  password !== securityAnswer
                );
              }
            ),
            passwordCannotContainUsernameValidation: helpers.withMessage(
              "Password cannot contain your username",
              () => {
                const { userName, password } = this.user;
                return (
                  userName === "" ||
                  password === "" ||
                  !password.includes(userName)
                );
              }
            ),
            valid: helpers.withMessage(
              "Please enter a strong password (One lower, one upper, one number, one special character, and at least 10 characters long)",
              (value: string) => {
                if (this.user.password === "") return true;
                const containsUppercase = /[A-Z]/.test(value);
                const containsLowercase = /[a-z]/.test(value);
                const containsNumber = /[0-9]/.test(value);
                const containsSpecial = /[#?!@$%^&*-]/.test(value);
                return (
                  containsUppercase &&
                  containsLowercase &&
                  containsNumber &&
                  containsSpecial
                );
              }
            ),
            sameAsPassword: helpers.withMessage(
              "Password does not match",
              sameAs(this.user.passwordConfirmation)
            ),
            minLength: minLength(10),
            maxLength: maxLength(128),
          }),
        },
        passwordConfirmation: {
          ...(isCreateStep && {
            required,
            sameAsPassword: helpers.withMessage(
              "Password does not match",
              sameAs(this.user.password)
            ),
          }),
        },
        email: {
          ...(isCreateStep && {
            required,
            maxLength: maxLength(80),
            validemail: helpers.withMessage(
              "Please enter a valid email address",
              email
            ),
          }),
        },
        securityQuestion: {
          ...(isCreateStep && {
            required,
            maxLength: maxLength(128),
          }),
        },
        securityAnswer: {
          ...(isCreateStep && {
            required,
            maxLength: maxLength(128),
          }),
        },
      },
    };
  },
  computed: {
    ...mapGetters(["getAutoLogoutMsg"]),
  },
  methods: {
    ...mapMutations(["setMaskedEmailAddress"]),
    hideErrors(field: string): void {
      this.v$.user[field].$touch();
      this.isFormInvalid = false;
    },
    backButtonHandler() {
      this.isFormInvalid = false;
      if (this.step === 2) {
        this.step = 1;
      }
    },
    buttonDesign(): string {
      const buttonStatus = this.v$.user.$invalid;
      if (this.step === 1) {
        return buttonStatus == true
          ? "btn-secondary w-100"
          : "btn-primary  w-100";
      } else {
        return buttonStatus == true ? "btn-secondary w-80" : "btn-primary w-80";
      }
    },
    async handleSubmit() {
      if (this.step === 1) {
        this.search();
      } else {
        this.create();
      }
    },
    async search(): Promise<void> {
      TokenService.logout();

      this.isFormInvalid = false;
      this.submitted = true;
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      const params = {
        ssnOrMemberID: this.user.ssn,
        zip: this.user.zip,
        dob: this.user.dob,
        lastName: this.user.lastName,
      };
      this.isSubmitDisabled = true;
      const result = await apiClient
        .searchParticipantController()
        .postApiSearchparticipantSearchparticipants(params);

      this.isSubmitDisabled = false;

      if (!result || result.error) {
        this.isFormInvalid = true;
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data?.match) {
        const token: string = result.data?.token || "";
        TokenService.setToken("SearchParticantToken", token);

        //not sure what this is used for yet
        if (result.data?.needEmailAddress) {
          this.$router.push({
            path: routesPath.participantEmail,
          });
        } else {
          this.setMaskedEmailAddress(result.data.emailAddress);

          //call for a code emailed and ask for it
          this.$router.push({
            name: routesPath.participantOTP,
          });
        }

        return;
      } else {
        this.isFormInvalid = true;
        const message: string = result.data?.noMatchMessage || "";
        this.validationMsg = message + messages.errorMsg;
        return;
      }
    },
    async create(): Promise<void> {
      this.isFormInvalid = false;
      this.submitted = true;
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      const params: apiClient.CreateAccountParameter = {
        username: this.user.userName,
        password: this.user.password,
        email: this.user.email,
        securityQuestion: this.user.securityQuestion,
        securityAnswer: this.user.securityAnswer,
      };
      this.isSubmitDisabled = true;
      const result = await apiClient
        .createAccountController()
        .postApiCreateaccountCreateaccount(params, {
          headers: { Authorization: "Bearer " + this.createAccountToken },
        });

      this.isSubmitDisabled = false;
      console.log(JSON.stringify(result.data));

      if (!result || result.error) {
        this.isFormInvalid = true;
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data?.created) {
        this.accountCreated = true;
        this.step = 0;
        return;
      } else {
        this.isFormInvalid = true;
        const notCreatedMessages: string[] =
          result.data?.notCreatedMessages || [];
        notCreatedMessages.forEach((msg) => {
          this.validationMsg = this.validationMsg + msg;
        });
        return;
      }
    },
  },
});
